const GLOBAL_MOTION_KEY = 'ay-tms:motion';
// The product is AY TMS; `km-tms:` is the old brand's prefix (Alan, 2026-07-31).
const LEGACY_MOTION_KEY = 'km-tms:motion';

// Motion is on (Alan, 2026-07-31 — supersedes the 2026-07-30 product-wide lock).
//
// The lock read "reduce motion" as "remove motion": every method was hardwired to 'off', the
// stored preference was overwritten with 'off' on every load, and setEnabled() ignored its
// argument. The login orb, the page entrances and the shell transitions all rendered as static
// images. The lag that prompted it came from a 27px three-layer folder icon repeated down 410
// rows, not from interface animation.
//
// This is a real switch again: on by default and the stored choice is honoured.
//
// The operating system's reduced-motion setting no longer vetoes it (Alan, 2026-08-01, after
// reporting Motion dead across the login page and the dashboard on a machine with Windows
// animations turned off). Recorded trade-off, because this one is not free: prefers-reduced-motion
// exists for people whose vestibular disorders make animation physically unpleasant, and ignoring
// it by default takes that protection away. Two things make it the right call here anyway — on
// Windows the flag is as often an OEM or power-saving default as a deliberate accessibility
// request, which is exactly the case that prompted this; and the in-app switch below is a real,
// persistent opt-out, so anyone who needs less motion turns it off once and it stays off.
//
// If that trade is ever judged wrong, restore the veto by putting systemPrefersReducedMotion()
// back in front of the three reads below.
function validateSharedOptions(root, storage) {
  if (!root?.dataset || !storage || typeof storage.getItem !== 'function'
    || typeof storage.setItem !== 'function') {
    throw new TypeError('Application Motion options are invalid.');
  }
}

const LOCK_CLEARED_KEY = 'ay-tms:motion:lock-cleared';

// The 2026-07-30 lock wrote 'off' into storage on every single load, so every browser that ever
// opened the app is carrying an 'off' that nobody chose. Reading it back as a preference would
// leave Motion exactly as dead as before for existing users while looking fixed on a new machine.
// This clears that value once, the first time the restored build runs, and records that it has
// done so — a genuine 'off' set afterwards is kept.
function clearLegacyLock(storage) {
  if (storage.getItem(LOCK_CLEARED_KEY) === '1') return;
  // Nothing is carried over from the old brand — the entry is simply removed.
  storage.removeItem(LEGACY_MOTION_KEY);
  if (storage.getItem(GLOBAL_MOTION_KEY) === 'off') storage.removeItem(GLOBAL_MOTION_KEY);
  storage.setItem(LOCK_CLEARED_KEY, '1');
}

function storedPreference(storage, key) {
  const value = storage.getItem(key);
  return value === 'on' || value === 'off' ? value : null;
}

function paint(root, enabled) {
  root.dataset.motion = enabled ? 'on' : 'off';
  return enabled;
}

export function applyInitialApplicationMotion({ root, storage }) {
  validateSharedOptions(root, storage);
  clearLegacyLock(storage);
  const enabled = storedPreference(storage, GLOBAL_MOTION_KEY) !== 'off';
  return paint(root, enabled);
}

export function createApplicationMotion({ root, storage, userKey }) {
  validateSharedOptions(root, storage);
  if (typeof userKey !== 'string' || !userKey.trim()) {
    throw new TypeError('Application Motion options are invalid.');
  }
  clearLegacyLock(storage);
  const listeners = new Set();
  // A per-user choice wins over the shared one, so two people on one machine do not inherit
  // each other's setting.
  const scopedKey = `${GLOBAL_MOTION_KEY}:${userKey.trim()}`;
  let enabled = (storedPreference(storage, scopedKey)
    ?? storedPreference(storage, GLOBAL_MOTION_KEY)) !== 'off';
  paint(root, enabled);

  function announce() {
    listeners.forEach((listener) => {
      try {
        listener(enabled);
      } catch {
        // One misbehaving subscriber must not stop the others being told.
      }
    });
  }

  return {
    enabled: () => enabled,
    setEnabled(next) {
      const wanted = next === true;
      if (wanted === enabled) return enabled;
      enabled = wanted;
      paint(root, enabled);
      const value = enabled ? 'on' : 'off';
      storage.setItem(scopedKey, value);
      storage.setItem(GLOBAL_MOTION_KEY, value);
      announce();
      return enabled;
    },
    subscribe(listener) {
      if (typeof listener !== 'function') throw new TypeError('Motion listener is invalid.');
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    destroy() { listeners.clear(); },
  };
}
